/**
 * Format a number as currency
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Format a number as a percentage
 */
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(3)}%`
}

export const calculateMaximumAffordability = ({
  annualIncome,
  downPayment,
  interestRate,
  amortizationYears,
  annualDebtPayments,
  dti = 36,
}: {
  annualIncome: number
  downPayment: number
  interestRate: number
  amortizationYears: number
  annualDebtPayments: number
  dti?: number
}): number => {
  // If no income, return down payment amount
  if (!annualIncome) return downPayment

  // Convert annual values to monthly
  const monthlyIncome = annualIncome / 12
  const monthlyDebtPayments = annualDebtPayments / 12
  const monthlyInterestRate = interestRate / 100 / 12
  const totalPayments = amortizationYears * 12

  // Calculate maximum monthly payment allowed based on DTI
  const maxMonthlyDebtPayment = monthlyIncome * (dti / 100)

  // Available for mortgage payment after existing debts
  const availableForMortgage = maxMonthlyDebtPayment - monthlyDebtPayments

  // If no money available for mortgage after debts, return down payment
  if (availableForMortgage <= 0) return downPayment

  // Calculate maximum loan amount
  let maxLoanAmount: number

  if (monthlyInterestRate === 0) {
    // Special case for 0% interest
    maxLoanAmount = availableForMortgage * totalPayments
  } else {
    // Standard mortgage payment formula solved for principal
    maxLoanAmount =
      (availableForMortgage * (1 - Math.pow(1 + monthlyInterestRate, -totalPayments))) / monthlyInterestRate
  }

  // Add down payment to get maximum home price
  return maxLoanAmount + downPayment
}

/**
 * Calculates down payment as a percentage of home price
 * @param downPaymentAmount Down payment amount in dollars
 * @param homePrice Total home price in dollars
 * @returns Down payment as a percentage of home price
 */
export const calculateDownPaymentPercentage = (downPaymentAmount: number, homePrice: number): number => {
  if (!homePrice || homePrice <= 0) {
    return 0
  }
  return (downPaymentAmount / homePrice) * 100
}

/**
 * Calculates the total monthly payment for a home, including mortgage and annual expenses.
 *
 * @param housePrice - Total price of the house
 * @param downPayment - Amount paid upfront
 * @param annualInterestRate - Annual interest rate as a percentage (e.g., 5 for 5%)
 * @param amortizationPeriodYears - Loan period in years
 * @param annualExpenses - Total annual expenses (e.g., taxes, insurance, HOA)
 * @returns The total monthly payment including annual expenses
 */
export const calculateMonthlyHomePayment = (
  housePrice: number,
  downPayment: number,
  annualInterestRate: number,
  amortizationPeriodYears: number,
  annualExpenses: number,
): number => {
  const loanAmount = Math.max(0, housePrice - downPayment)
  const monthlyInterestRate = annualInterestRate / 100 / 12
  const totalPayments = amortizationPeriodYears * 12

  let monthlyMortgage = 0

  if (monthlyInterestRate === 0) {
    monthlyMortgage = loanAmount / totalPayments
  } else {
    monthlyMortgage = (loanAmount * monthlyInterestRate) / (1 - Math.pow(1 + monthlyInterestRate, -totalPayments))
  }

  const monthlyExpenses = annualExpenses / 12

  return monthlyMortgage + monthlyExpenses
}

/**
 * Calculates the total monthly mortgage payment including all costs
 */
export const calculateTotalMonthlyPayment = ({
  homePrice,
  downPayment,
  interestRate,
  amortizationYears,
}: {
  homePrice: number
  downPayment: number
  interestRate: number
  amortizationYears: number
}): number => {
  // Calculate loan amount
  const loanAmount = Math.max(0, homePrice - downPayment)

  // Convert to monthly values
  const monthlyInterestRate = interestRate / 100 / 12
  const totalPayments = amortizationYears * 12

  // Calculate monthly mortgage payment
  let monthlyMortgagePayment: number

  if (monthlyInterestRate === 0) {
    // Special case for 0% interest
    monthlyMortgagePayment = loanAmount / totalPayments
  } else {
    // Standard mortgage payment formula
    monthlyMortgagePayment =
      (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, totalPayments)) /
      (Math.pow(1 + monthlyInterestRate, totalPayments) - 1)
  }

  // Return either just the mortgage payment or mortgage + all debt payments
  return monthlyMortgagePayment
}
